
import { pipeline, env } from '@huggingface/transformers';
import { logger } from '../utils/logger';

// Skip local checks for now since we are in a browser environment essentially
// (though in dev we might be in node, but target is browser)
env.allowLocalModels = false;
env.useBrowserCache = true;

// Configure the ONNX backend to use the bundled WASM files instead of retrieving from CDN.
// We must specify the WASM paths via chrome.runtime.getURL because we bundled them into /wasm during build via static-copy.
if (env.backends?.onnx?.wasm) {
  env.backends.onnx.wasm.wasmPaths = chrome.runtime.getURL('wasm/');
}

class EmbeddingService {
  private static instance: EmbeddingService;
  private extractor: any = null;
  private modelName = 'Xenova/all-MiniLM-L6-v2';

  private constructor() {}

  public static getInstance(): EmbeddingService {
    if (!EmbeddingService.instance) {
      EmbeddingService.instance = new EmbeddingService();
    }
    return EmbeddingService.instance;
  }

  public async init() {
    if (!this.extractor) {
      const wasmPath = env.backends?.onnx?.wasm?.wasmPaths;
      logger.info(`[EmbeddingService] Loading model "${this.modelName}" (dtype:q8). WASM path: ${wasmPath}`);
      try {
        this.extractor = await pipeline('feature-extraction', this.modelName, { dtype: 'q8' });
        logger.info(`[EmbeddingService] Model "${this.modelName}" loaded successfully.`);
      } catch (e: unknown) {
        const msg = e instanceof Error ? e.message : String(e);
        const stack = e instanceof Error && e.stack ? `\n${e.stack}` : '';
        logger.error(`[EmbeddingService] Failed to load model "${this.modelName}": ${msg}${stack}`);
        throw e;
      }
    }
  }

  public async generateEmbedding(text: string): Promise<number[]> {
    await this.init();

    // Mean pooling
    const output = await this.extractor(text, { pooling: 'mean', normalize: true });

    // Output is a Tensor, we want a regular array
    const result = Array.from(output.data) as number[];

    // Explicitly dispose of the tensor to prevent WASM memory leaks
    if (output.dispose) {
        output.dispose();
    }

    return result;
  }

  /**
   * Generate embeddings for multiple texts in a single batched ONNX inference call.
   * Significantly faster than N individual calls — the transformer's matrix operations
   * are vectorised across the batch dimension by the ONNX runtime.
   *
   * @returns Array of 384-dim vectors in the same order as the input texts.
   */
  public async generateEmbeddings(texts: string[]): Promise<number[][]> {
    if (texts.length === 0) return [];
    await this.init();

    // FeatureExtractionPipeline accepts string[]. With pooling:'mean' the output Tensor
    // has dims [batch_size, hidden_size]; output.data is a flat Float32Array of length
    // batch_size * hidden_size.
    const output = await this.extractor(texts, { pooling: 'mean', normalize: true });

    const hiddenSize: number = output.dims[output.dims.length - 1];
    const data = output.data as Float32Array;
    const results: number[][] = [];
    for (let i = 0; i < texts.length; i++) {
      // subarray is a zero-copy view (no intermediate allocation); Array.from
      // then extracts values into a plain JS array.
      results.push(Array.from(data.subarray(i * hiddenSize, (i + 1) * hiddenSize)));
    }

    if (output.dispose) output.dispose();

    return results;
  }
}

export const embeddingService = EmbeddingService.getInstance();
